/**
 * 通过 Vite `?url` 引入字体文件，确保构建后 src 正确；并 preload + @font-face 注入 document，
 * 避免仅靠独立 CSS 时 url 未被打包或字体懒加载导致 Network 里看不到请求。
 *
 * HiPrint（vue-plugin-hiprint）在隐藏 iframe 内排版打印，不会继承主页面样式，需通过
 * `$.fn.hiwprint.defaults.styleHandler` 注入同源绝对 URL 的 @font-face（见 patchHiPrintKhmerFont）。
 */
import khmerFontUrl from '../assets/fonts/KhmerOS_muollight.ttf?url';

/** 与下方 @font-face 的 font-family 保持一致（打印模板 item.options.fontFamily 使用） */
export const KHMER_OS_MUOL_LIGHT_FONT_FAMILY = 'Khmer OS Muol Light';

const PRELOAD_MARK = 'link[data-app-khmer-font-preload]';

/** 打印 iframe 内建议使用相对当前页的绝对地址，避免部分浏览器解析字体失败 */
function khmerFontSrcForCss(): string {
  if (typeof window === 'undefined') {
    return khmerFontUrl;
  }
  try {
    return new URL(khmerFontUrl, window.location.href).href;
  } catch {
    return khmerFontUrl;
  }
}

/** @font-face 纯 CSS，可供主文档 style 与 HiPrint styleHandler 复用 */
export function buildKhmerFontFaceCssText(): string {
  const src = khmerFontSrcForCss();
  return `@font-face{font-family:'${KHMER_OS_MUOL_LIGHT_FONT_FAMILY}';src:url('${src}') format('truetype');font-weight:normal;font-style:normal;font-display:swap;}`;
}

/** HiPrint 拼进打印 HTML head 的片段（与 print-lock.css 等一并写入 iframe） */
export function getKhmerFontFacePrintStyleHtml(): string {
  return `<style type="text/css" data-app-khmer-print-font>${buildKhmerFontFaceCssText()}</style>`;
}

let hiPrintKhmerFontPatched = false;

/**
 * vue-plugin-hiprint 使用 jQuery hiwprint：通过 styleHandler 往打印 iframe 注入 Khmer 字体。
 * 须在动态 import('vue-plugin-hiprint') 之后调用（此时 window.jQuery 已就绪）。
 */
export function patchHiPrintKhmerFont(): void {
  if (hiPrintKhmerFontPatched) {
    return;
  }
  interface JqHiPrint {
    fn?: { hiwprint?: { defaults?: { styleHandler?: () => string } } };
  }
  const $ = (globalThis as unknown as { jQuery?: JqHiPrint }).jQuery;
  if (!$?.fn?.hiwprint?.defaults) {
    return;
  }
  const prev = $.fn.hiwprint.defaults.styleHandler;
  const fragment = getKhmerFontFacePrintStyleHtml();
  $.fn.hiwprint.defaults.styleHandler = () => {
    const prevHtml = typeof prev === 'function' ? (prev() ?? '') : '';
    return `${prevHtml}${fragment}`;
  };
  hiPrintKhmerFontPatched = true;
}

export function registerKhmerFont(): void {
  if (typeof document === 'undefined') {
    return;
  }
  if (document.head.querySelector(PRELOAD_MARK)) {
    return;
  }

  const preload = document.createElement('link');
  preload.rel = 'preload';
  preload.href = khmerFontUrl;
  preload.as = 'font';
  preload.type = 'font/ttf';
  preload.crossOrigin = 'anonymous';
  preload.setAttribute('data-app-khmer-font-preload', '');
  document.head.appendChild(preload);

  const style = document.createElement('style');
  style.setAttribute('data-app-khmer-font-face', '');
  style.textContent = buildKhmerFontFaceCssText();
  document.head.appendChild(style);

  /** 触发浏览器下载字体（否则部分浏览器仅在首次绘制到该 font-family 时才请求） */
  requestAnimationFrame(() => {
    if (!document.body) return;
    const probe = document.createElement('span');
    probe.setAttribute('aria-hidden', 'true');
    probe.style.cssText = `position:absolute;left:0;top:0;clip:rect(0,0,0,0);font-family:'${KHMER_OS_MUOL_LIGHT_FONT_FAMILY}',sans-serif;font-size:16px;`;
    probe.textContent = 'ក';
    document.body.appendChild(probe);
    void probe.offsetHeight;
    probe.remove();
  });
}
